import { Button } from '@mui/material';
import { closeSnackbar, type OptionsObject, type SnackbarKey } from 'notistack';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Snapshot, useRecoilCallback, useRecoilValue } from 'recoil';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { type RoiId, StructureSetId } from '~/domain/changes/ids';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { isApprovalStatusEditable } from '~/domain/dicom/dicomConstants';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { runRoiCommand } from '~/hooks/domain/useRoiCommands';
import {
    bulkRoiSelectionActiveState,
    StructureColorEditSession,
    StructureColorEditSource,
    structureColorEditOfState,
    structureColorEditSessionState,
} from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { formatColorRgb } from '~/utils/color';
import { dicomColorsEqual } from '~/utils/colorUtils';
import {
    enqueueSnackbar,
    NOTIFICATION_AUTO_HIDE_MS,
} from '~/utils/notifications';

type Anchor = StructureColorEditSession['anchor'];

/** Something the user did to the colour edit. */
export type StructureColorEditEvent =
    /**
     * Opens an edit. One already open for this structure continues; one
     * open for another structure is discarded.
     */
    | { type: 'open'; session: Omit<StructureColorEditSession, 'pending'> }
    /** A colour dragged or typed; null returns to the colour at open. */
    | { type: 'setPending'; color: DicomColor | null }
    /** Enter or Apply: the only event that saves. */
    | { type: 'confirm' }
    /** Any other way out, or the structure no longer being editable. */
    | { type: 'cancel' };

/** A colour to save for a structure of a structure set. */
export type StructureColorCommit = Pick<
    StructureColorEditSession,
    'structureSetId' | 'roiId'
> & { color: DicomColor };

/**
 * The colour edit's state machine: the next session and the one save, if
 * any, that an event calls for. Pure — the caller performs the save. Only
 * confirming a colour that differs from the one at open saves.
 */
export function transitionStructureColorEdit(
    session: StructureColorEditSession | null,
    event: StructureColorEditEvent
): {
    session: StructureColorEditSession | null;
    commit: StructureColorCommit | null;
} {
    switch (event.type) {
        case 'open': {
            const continued =
                session?.structureSetId === event.session.structureSetId &&
                session.roiId === event.session.roiId;
            return {
                session: {
                    ...event.session,
                    colorAtOpen: continued
                        ? session.colorAtOpen
                        : event.session.colorAtOpen,
                    pending: continued ? session.pending : null,
                },
                commit: null,
            };
        }
        case 'setPending':
            return {
                session: session && { ...session, pending: event.color },
                commit: null,
            };
        case 'confirm': {
            const color = session?.pending;
            const changed =
                session &&
                color &&
                !dicomColorsEqual(color, session.colorAtOpen);
            return {
                session: null,
                commit: changed
                    ? {
                          structureSetId: session.structureSetId,
                          roiId: session.roiId,
                          color,
                      }
                    : null,
            };
        }
        case 'cancel':
            return { session: null, commit: null };
    }
}

/**
 * Marks a control whose own click opens and closes the colour edit, so
 * the popover does not take a press on it for a press outside.
 */
export const COLOR_EDIT_OPENER_ATTRIBUTE = 'data-color-edit-opener';

/**
 * Why a structure's colour cannot be edited as things stand, as the id of
 * the message that says so, or undefined when it can. A bulk selection
 * withholds the edit, as it does every action on a single structure;
 * otherwise approval does.
 */
export const colorEditWithheldReasonId = (
    roi: Pick<RoiReadModel, 'approvalStatus'>,
    isBulkSelectionActive: boolean
): string | undefined =>
    isBulkSelectionActive
        ? 'tool.disabled.bulkSelection'
        : isApprovalStatusEditable(roi.approvalStatus)
          ? undefined
          : 'structure.approved';

/** How long the report that an Undo was done stays up. */
const COLOR_RESTORED_REPORT_MS = 3000;

/** The change report on screen: a newer change replaces it. */
let liveReportKey: SnackbarKey | undefined;

/**
 * The structure in the current structure set, or undefined — also when
 * `structureSetId` is given and is not the current set.
 */
function currentRoi(
    snapshot: Snapshot,
    roiId: RoiId,
    structureSetId?: StructureSetId
) {
    const set = snapshot.getLoadable(currentStructureSetState).getValue();
    return structureSetId === undefined || set?.id === structureSetId
        ? set?.roiArray.find((r) => r.roiId === roiId)
        : undefined;
}

/**
 * The structure's row in the structure list, for placing the popover
 * beside it; `fallback` when the row is not rendered or is scrolled out
 * of the list's view.
 */
export function structureRowAnchor(
    roiId: RoiId,
    fallback: HTMLElement | null
): HTMLElement | null {
    const row = document.querySelector<HTMLElement>(
        `[data-structure-row][data-roi-id="${roiId}"]`
    );
    if (!row) {
        return fallback;
    }
    const { top, height } = row.getBoundingClientRect();
    const view = row
        .closest('[data-structure-list]')
        ?.getBoundingClientRect() ?? { top: 0, bottom: window.innerHeight };
    const centre = top + height / 2;
    return centre >= view.top && centre <= view.bottom ? row : fallback;
}

/**
 * The one controller for editing a structure's display colour: every
 * control that opens the colour popover, and the popover itself, goes
 * through it.
 *
 * Only a confirmed edit, or the Undo of one, saves — once, through
 * RoiService, the path the unsaved-changes guard and the save workflow
 * observe. Colours shown while an edit is open are display overrides. An
 * edit and its Undo belong to the structure set they were made in.
 */
export function useStructureColorEdit() {
    const intl = useIntl();
    // Nullable: the controls using this mount before a patient is loaded.
    const session = useSessionOrNull();

    const notify = useCallback(
        (id: string, structureName: string, options: OptionsObject) =>
            enqueueSnackbar(
                intl.formatMessage({ id }, { structureName }),
                options
            ),
        [intl]
    );

    /**
     * Saves the colour, unless its structure set is no longer current or
     * the colour is already the saved one. Returns the structure as it
     * was before the save, or null when nothing was saved; a refusal has
     * then already been reported.
     */
    const saveColor = useRecoilCallback(
        ({ snapshot }) =>
            ({ structureSetId, roiId, color }: StructureColorCommit) => {
                const roi = currentRoi(snapshot, roiId, structureSetId);
                if (!roi || dicomColorsEqual(roi.color, color)) {
                    return null;
                }
                const held = session?.patient
                    .getStructureSetById(structureSetId)
                    ?.getRoiById(roiId);
                if (!session || !held) {
                    notify('colorPicker.saveFailed', roi.name, {
                        variant: 'error',
                    });
                    return null;
                }
                const saved = runRoiCommand(() =>
                    session.roiService.setColor({ id: roiId }, color)
                );
                return saved ? roi : null;
            },
        [notify, session]
    );

    /**
     * Restores the colour a change replaced, provided the structure is
     * still in the current set with the colour that change gave it.
     * Otherwise declines, saying which of the two does not hold, rather
     * than overwrite a colour it was not offered for.
     */
    const undoColor = useRecoilCallback(
        ({ snapshot }) =>
            (change: StructureColorCommit, from: DicomColor) => {
                const roi = currentRoi(
                    snapshot,
                    change.roiId,
                    change.structureSetId
                );
                if (!roi || !dicomColorsEqual(roi.color, change.color)) {
                    notify(
                        roi
                            ? 'colorPicker.undoUnavailable'
                            : 'colorPicker.undoStructureGone',
                        '',
                        { variant: 'warning' }
                    );
                } else if (saveColor({ ...change, color: from })) {
                    notify('colorPicker.colorRestored', roi.name, {
                        variant: 'info',
                        autoHideDuration: COLOR_RESTORED_REPORT_MS,
                    });
                }
            },
        [notify, saveColor]
    );

    const dispatch = useRecoilCallback(
        ({ snapshot, set }) =>
            (event: StructureColorEditEvent) => {
                const { session: next, commit } = transitionStructureColorEdit(
                    snapshot
                        .getLoadable(structureColorEditSessionState)
                        .getValue(),
                    event
                );
                set(structureColorEditSessionState, next);
                const before = commit && saveColor(commit);
                if (!commit || !before) {
                    return;
                }
                if (liveReportKey !== undefined) {
                    closeSnackbar(liveReportKey);
                }
                liveReportKey = notify(
                    'colorPicker.colorChanged',
                    before.name,
                    {
                        variant: 'success',
                        // The same text as the report it replaces: not a duplicate.
                        preventDuplicate: false,
                        // Long enough to reach the Undo it carries.
                        autoHideDuration: NOTIFICATION_AUTO_HIDE_MS,
                        action: (key: SnackbarKey) => (
                            <Button
                                color="inherit"
                                size="small"
                                data-testid="structure-color-undo"
                                onClick={() => {
                                    closeSnackbar(key);
                                    undoColor(commit, before.color);
                                }}
                            >
                                {intl.formatMessage({ id: 'colorPicker.undo' })}
                            </Button>
                        ),
                    }
                );
            },
        [intl, notify, saveColor, undoColor]
    );

    /**
     * Opens the colour edit for a structure of the current structure set.
     * Refused — returning false — for a structure that is not in it or
     * whose colour edit is withheld. Focus returns to `returnFocusTo` when
     * the edit closes.
     */
    const open = useRecoilCallback(
        ({ snapshot }) =>
            (
                roiId: RoiId,
                source: StructureColorEditSource,
                anchor: Anchor,
                returnFocusTo: Element | null = document.activeElement
            ): boolean => {
                const set = snapshot
                    .getLoadable(currentStructureSetState)
                    .getValue();
                const roi = currentRoi(snapshot, roiId);
                if (
                    !set ||
                    !roi ||
                    colorEditWithheldReasonId(
                        roi,
                        snapshot
                            .getLoadable(bulkRoiSelectionActiveState)
                            .getValue()
                    ) !== undefined
                ) {
                    return false;
                }
                dispatch({
                    type: 'open',
                    session: {
                        structureSetId: set.id,
                        roiId,
                        source,
                        anchor,
                        returnFocusTo:
                            returnFocusTo instanceof HTMLElement
                                ? returnFocusTo
                                : null,
                        colorAtOpen: roi.color,
                    },
                });
                return true;
            },
        [dispatch]
    );

    /**
     * Opens the edit, or closes it with nothing saved when it is already
     * open for this structure: a second click on the control that opened it.
     */
    const toggle = useRecoilCallback(
        ({ snapshot }) =>
            (
                roiId: RoiId,
                source: StructureColorEditSource,
                anchor: Anchor
            ) => {
                const current = snapshot
                    .getLoadable(structureColorEditSessionState)
                    .getValue();
                if (current?.roiId === roiId) {
                    dispatch({ type: 'cancel' });
                } else {
                    open(roiId, source, anchor);
                }
            },
        [dispatch, open]
    );

    return useMemo(
        () => ({
            open,
            toggle,
            setPending: (color: DicomColor | null) =>
                dispatch({ type: 'setPending', color }),
            confirm: () => dispatch({ type: 'confirm' }),
            cancel: () => dispatch({ type: 'cancel' }),
        }),
        [open, toggle, dispatch]
    );
}

/**
 * The colour to show for a structure: the unsaved colour of its open
 * colour edit, otherwise its saved colour.
 */
export const useStructureDisplayColor = (roi: RoiReadModel): DicomColor =>
    useRecoilValue(structureColorEditOfState(roi.roiId))?.pending ?? roi.color;

/** {@link colorEditWithheldReasonId} of a structure as things stand. */
export const useColorEditWithheldReasonId = (roi: RoiReadModel) =>
    colorEditWithheldReasonId(roi, useRecoilValue(bulkRoiSelectionActiveState));

/**
 * What a button that opens a structure's colour edit needs: the colour to
 * show, why the edit is withheld if it is, the toggle, and the attributes
 * that name it and mark it as the edit's opener.
 */
export function useStructureColorOpener(
    roi: RoiReadModel,
    source: StructureColorEditSource
) {
    const intl = useIntl();
    const edit = useRecoilValue(structureColorEditOfState(roi.roiId));
    const withheldReasonId = useColorEditWithheldReasonId(roi);
    const { toggle } = useStructureColorEdit();
    const color = edit?.pending ?? roi.color;
    return {
        color,
        withheldReasonId,
        toggle: (anchor: Anchor) => toggle(roi.roiId, source, anchor),
        openerProps: {
            type: 'button',
            'aria-label': intl.formatMessage(
                { id: 'colorPicker.buttonLabel' },
                { structureName: roi.name, color: formatColorRgb(color) }
            ),
            'aria-haspopup': 'dialog',
            'aria-expanded': edit !== null,
            [COLOR_EDIT_OPENER_ATTRIBUTE]: '',
        } as const,
    };
}
